import { Autocomplete, Button, Checkbox, Chip, LinearProgress, Paper, Popper, styled, TextField, Tooltip } from '@mui/material'
import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { AddCircleOutlineRounded } from '@mui/icons-material'
import { SmartDialog } from '../utilities/SmartDialog'
import { useSmartTranslation } from '../hooks/useSmartTranslation'
import Proptypes from 'prop-types'
import { useTheme } from '@emotion/react'

const getOptionDisplayLabel = (option) => {
  if (option === null || option === undefined) return ''
  if (typeof option === 'object') {
    return String(option.name ?? option.label ?? option.code ?? '')
  }
  return String(option)
}

const CustomPopper = styled((props) => {
  const anchorWidth = props.anchorEl?.clientWidth ?? 0

  return (
    <Popper
      {...props}
      placement='bottom-start'
      style={{
        ...props.style,
        minWidth: anchorWidth,
        width: 'max-content'
      }}
    />
  )
})({
  '& .MuiAutocomplete-paper': {
    maxWidth: 500
  }
})

/**
* SmartSelect component
* @param {Object} props Props object
* @param {String[]} props.selectableOptions Selectable options.
* @param {(string|string[])} props.value Current selected values.
* @param {Boolean} props.disableClearable If true, the input can't be cleared. *Default value true*
* @param {function(String)} props.onChange Method that will be exetuted after changin selected value.
* @param {String} props.label Text that will appear on the textfield.
* @param {Boolean}  props.error If true it will show error on the textfield.
* @param {Object} props.content The content that will open on the dialog *Add record*
* @param {React.Dispatch<React.SetStateAction<boolean>>}  props.setReloadAutocompleteOptions useState set parameter to refresh selectable items. *After  adding record*
* @param {Boolean} props.multiple If true the component will have the multiSelection mode turn on, otherwise. *Default value false*
* @param {Function} props.functionToChangeTheValueWhenAddANewElement Function that will be executed after closing the dialog that opens *Adding record*
* @param {Boolean} props.readOnly If true, the component will be readOnly. *Default value false*
* @param {Boolean} props.addButtonVisible If true, Add button will be visible. *Default value false*
* @param {Boolean} props.disabled If true, the component will be disabled. *Default value false*
* @param {Boolean} props.autoSelectFirst If true, the component will select the selectable option if there is only one. *Default value true*
* @param {String} props.minWidth SmartSelect component minWidth. *Default value 5rem*
* @param {Function} props.addDialogClicked Function to be called when add button is clicked. If this is specified the content dialog wont be used
* @param {'small'|'medium'} props.size Component size
* @param {String} props.width SmartSelect component width. *Default undefined*
* @param {String} props.helperText Helper text that will be shown on the textfield. *Default value is empty*
* @param {('initial'|'small'|'medium')} props.fontSize Font size. *Default value is initial*
* @param {'outlined'|'filled'|'standard'} props.variant Component variant. *Default value is outlined*
* @param {Boolean} props.disableAddDialogButtons If true, the dialog buttons of SmartDialog will be disabled. *Default value false*
* @param {Boolean} props.forceCheckError If true, will force to check if the value is correct
* @param {Function} props.iconFunction Function to get the icon for each option. Takes the option object and returns an icon component. *Default value undefined*
* @param {Function} props.optionLabelTransform Optional function (option) => string to override the display label for each option.
*   The internal value is never affected — only the visible text in the dropdown and selected input.
*/

const SmartSelect = ({
  selectableOptions,
  value = null,
  disableClearable = true,
  onChange,
  label,
  error,
  content,
  setReloadAutocompleteOptions,
  multiple = false,
  functionToChangeTheValueWhenAddANewElement,
  readOnly = false,
  addButtonVisible = false,
  disabled = false,
  autoSelectFirst = true,
  minWidth = '5rem',
  addDialogClicked = null,
  size = 'medium',
  width,
  helperText = '',
  fontSize = 'initial',
  variant = 'outlined',
  sortValues = true,
  onClose,
  optionStyle = { wordBreak: 'normal' },
  disabledOptions = [],
  toolTip = false,
  toolTipValue = '',
  addDialogAccept = false,
  addDialogClose = true,
  onAddDialogAccept = null,
  addDialogTitle = { render: null },
  disableAddDialogButtons = false,
  limitTags = 2,
  forceCheckError,
  iconFunction,
  sourceWithSearch,
  required = false,
  optionLabelTransform = null
}) => {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()

  const SELECT_ALL = t_('Select All')
  const [openDialog, setOpenDialog] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [firstTimeAutoSelect, setFirstTimeAutoSelect] = useState(true)
  const [disabledValues, setDisabledValues] = useState([])
  const [hasError, setHasError] = useState(false)
  const [asyncOptions, setAsyncOptions] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const check = () => {
      if (typeof error === 'function') {
        setHasError(Boolean(error(value)))
      } else {
        setHasError(error)
      }
    }

    check()
  }, [forceCheckError, value, error])

  useEffect(() => {
    let active = true

    // If no onSearch prop is provided nothing done. We will use just the options
    if (!sourceWithSearch) return undefined

    const timer = setTimeout(async () => {
      setLoading(true)
      try {
        // When a item is selected we will show other options as if nothing is selected
        const results = await sourceWithSearch(inputValue === value ? '' : inputValue)
        if (active) setAsyncOptions(results.slice(0, 10)) // Keep only 10 items
      } catch (err) {
        console.error('Search failed', err)
      } finally {
        if (active) setLoading(false)
      }
    }, 300)

    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [inputValue, sourceWithSearch, value])

  const resolveDisplayLabel = useCallback(
    (option) => optionLabelTransform ? optionLabelTransform(option) : getOptionDisplayLabel(option),
    [optionLabelTransform]
  )

  const getSortableLabel = useCallback((option) => getOptionDisplayLabel(option), [])

  const sortByLabel = useCallback((items = []) => {
    return items
      .map((item, index) => ({ item, index }))
      .sort((a, b) => {
        const labelA = getSortableLabel(a.item).toLowerCase()
        const labelB = getSortableLabel(b.item).toLowerCase()
        const compare = labelA.localeCompare(labelB)
        if (compare !== 0) return compare
        return a.index - b.index
      })
      .map(({ item }) => item)
  }, [getSortableLabel])

  const addButton = useCallback(({ children, ...other }) => {
    const handleOpen = () => {
      if (addDialogClicked) {
        addDialogClicked()
        return
      }
      setOpenDialog(true)
    }
    return (
      <Paper {...other}>

        {children}
        {addButtonVisible && (
          <Button
            fullWidth
            sx={{
              borderTopWidth: 1,
              borderTopColor: theme.palette.primary.main,
              borderTopStyle: 'solid',
              backgroundColor: theme.palette.background.paper
            }}
            onClick={handleOpen}
            onMouseDown={e => e.preventDefault()}
          >
            <AddCircleOutlineRounded color='primary' /> {t_('ADD RECORD')}
          </Button>
        )}
        {loading && <LinearProgress color='primary' sx={{ width: '100%', height: 3 }} />}
      </Paper>
    )
  }, [addButtonVisible, addDialogClicked, t_, theme.palette.primary.main, theme.palette.background.paper, loading])

  const handleOptionsReload = () => {
    setReloadAutocompleteOptions?.(true)
    setOpenDialog(false)
    functionToChangeTheValueWhenAddANewElement?.()
  }

  const displayData = useMemo(() => {
    if (!multiple) {
      if (value !== '' && value !== undefined) return value
      return null
    }
    if (value !== '' && value !== undefined) return sortValues ? sortByLabel(value) : [...value]
    return []
  }, [multiple, value, sortValues, sortByLabel])

  const options = useMemo(() => {
    if (readOnly) return multiple ? displayData : [displayData]

    let opts = sourceWithSearch ? asyncOptions : selectableOptions
    if (sortValues && !sourceWithSearch) {
      if (opts === null || opts === undefined) {
        opts = []
      }
      opts = sortByLabel(opts)
    }
    if (multiple) return [SELECT_ALL, ...opts]
    return opts
  }, [SELECT_ALL, multiple, selectableOptions, sortValues, displayData, readOnly, sortByLabel, sourceWithSearch, asyncOptions])

  useEffect(() => {
    if (disabledOptions instanceof Promise) {
      disabledOptions.then(
        data => setDisabledValues(data)
      )
    }
  }, [disabledOptions])

  const isDisabled = useCallback(
    opt => disabledValues.includes(opt) && opt !== SELECT_ALL,
    [disabledValues, SELECT_ALL]
  )

  const onChangeMultiSelectedValue = newValue => {
    let selected = []
    if (newValue.includes(SELECT_ALL)) {
      const enabled = selectableOptions?.filter(o => !isDisabled(o))
      selected = newValue?.length - 1 === enabled?.length ? [] : enabled
    } else {
      selected = newValue?.filter(o => o !== SELECT_ALL && !isDisabled(o))
    }
    setInputValue('')
    onChange(selected)
  }

  const checkSelectAll = () =>
    value?.length === selectableOptions?.filter(o => !isDisabled(o)).length && value?.length > 0

  useEffect(() => {
    if (
      autoSelectFirst &&
      !multiple &&
      (value === null || value === '') &&
      selectableOptions?.length === 1 &&
      firstTimeAutoSelect
    ) {
      onChange(selectableOptions[0])
    }
  }, [autoSelectFirst, multiple, value, selectableOptions, onChange, firstTimeAutoSelect])

  useEffect(() => {
    if (value !== null && value !== '' && value !== undefined && firstTimeAutoSelect) {
      setFirstTimeAutoSelect(false)
    }
  }, [value, firstTimeAutoSelect])

  const onBlur = event => {
    if (multiple && value?.find(val => val === inputValue) !== null) setInputValue('')
    event.preventDefault()
  }

  return (
    <div onClick={e => e.stopPropagation()} style={{ width: width ?? '100%' }}>
      <Autocomplete
        size={size}
        PopperComponent={CustomPopper}
        disableCloseOnSelect={multiple}
        popupIcon={null}
        disabled={disabled}
        limitTags={limitTags}
        readOnly={readOnly}
        multiple={multiple}
        disableClearable={disableClearable}
        getOptionLabel={resolveDisplayLabel}
        options={options}
        value={displayData}
        loading={loading}
        filterOptions={sourceWithSearch ? (x) => x : undefined}
        inputValue={multiple || sourceWithSearch ? inputValue : undefined}
        onInputChange={multiple || sourceWithSearch
          ? (_, v, reason) => {
              if (
                reason === 'input' ||
                reason === 'clear' ||
                (!multiple && reason === 'reset')
              ) {
                setInputValue(v)
              }
            }
          : undefined}
        autoComplete
        autoHighlight
        onClose={onClose}
        includeInputInList
        fullWidth
        getOptionDisabled={isDisabled}
        renderTags={readOnly && multiple ? (value, getTagProps) => value.map((option, index) => <Chip key={index} icon={iconFunction ? iconFunction(option) : undefined} label={resolveDisplayLabel(option)} {...getTagProps({ index })} />) : undefined}
        renderOption={(props, option, { selected }) =>
          multiple
            ? (
              <li
                {...props}
                key={getOptionDisplayLabel(option) || JSON.stringify(option)}
                style={{
                  ...optionStyle,
                  color: isDisabled(option) ? theme.palette.text.disabled : 'inherit'
                }}
              >
                <Checkbox
                  disabled={isDisabled(option)}
                  checked={option === SELECT_ALL ? checkSelectAll() : selected}
                />
                {iconFunction && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: 8 }}>
                    {iconFunction(option)}
                  </span>
                )}
                {resolveDisplayLabel(option)}
              </li>)
            : (
              <li
                {...props} key={getOptionDisplayLabel(option)}
              >
                {resolveDisplayLabel(option)}
              </li>)}
        onChange={(event, newValue) => { multiple ? onChangeMultiSelectedValue(newValue) : onChange(newValue) }}
        renderInput={(params) =>
          <Tooltip title={!toolTip ? null : (toolTipValue ?? value)} placement='top' disableHoverListener={!toolTip}>
            <TextField
              helperText={helperText}
              onBlur={onBlur}
              variant={readOnly ? 'standard' : variant}
              fullWidth
              {...params}
              InputProps={
                readOnly
                  ? { ...params.InputProps, readOnly: true, disableUnderline: true }
                  : {
                      ...params.InputProps,
                      readOnly: false,
                      style: { paddingRight: '1.7rem', width, minWidth, fontSize }
                    }
              }
              error={hasError}
              label={label}
              InputLabelProps={{
                shrink: true,
                sx: { fontSize, '&.MuiOutlinedInput-notchedOutline': { fontSize } }
              }}
              required={!readOnly && !disabled && disableClearable && required}
            />
          </Tooltip>}
        PaperComponent={addButton}
      />

      {openDialog && (
        <SmartDialog
          title={addDialogTitle}
          setOpen
          renderComponent={content}
          close={addDialogClose}
          accept={addDialogAccept}
          cancel={false}
          closeCallback={handleOptionsReload}
          acceptCallback={async () => {
            const result = await onAddDialogAccept?.()
            if (result !== false) setOpenDialog(false)
          }}
          disableButtons={disableAddDialogButtons}
        />
      )}
    </div>
  )
}

SmartSelect.propTypes = {
  selectableOptions: Proptypes.arrayOf(Proptypes.string),
  value: Proptypes.oneOfType([
    Proptypes.arrayOf(Proptypes.string).isRequired,
    Proptypes.string.isRequired
  ]),
  disableClearable: Proptypes.bool,
  onChange: Proptypes.func,
  label: Proptypes.string,
  error: Proptypes.bool,
  content: Proptypes.object,
  multiple: Proptypes.bool,
  functionToChangeTheValueWhenAddANewElement: Proptypes.func,
  readOnly: Proptypes.bool,
  addButtonVisible: Proptypes.bool,
  disabled: Proptypes.bool,
  autoSelectFirst: Proptypes.bool,
  minWidth: Proptypes.string,
  addDialogClicked: Proptypes.func,
  size: Proptypes.string,
  width: Proptypes.string,
  helperText: Proptypes.string,
  disabledOptions: Proptypes.object,
  disableAddDialogButtons: Proptypes.bool
}

export default SmartSelect
